import { forwardRef, useCallback, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { dropdownColors } from 'app/colors';
import { layers } from 'app/spacing';
import Icon from 'components/Kizen/Icon';
import { TextEllipsisWithTooltip } from 'components/Kizen/Table';
import { applyRef } from '../props';

const Label = styled.label`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  margin-bottom: unset; // Undo global label style
  // These two ensure that the height of the parent doesn't
  // change when the box is checked versus unchecked. Just an inline sizing quirk.
  line-height: 0;
  vertical-align: top;
  > input {
    position: absolute;
    opacity: 0;
    z-index: ${layers.content(0)};
    & + * {
      flex-shrink: 0;
      .CheckboxCheckIcon,
      .CheckboxIndeterminateIcon {
        display: none;
      }
    }
    &:checked + * {
      border-color: var(--color-button-secondary-hover);
      background-color: var(--color-button-secondary-hover);
      .CheckboxCheckIcon {
        display: initial;
      }
      .CheckboxIndeterminateIcon {
        display: none;
      }
    }
    &:indeterminate + * {
      border-color: var(--color-button-secondary-hover);
      background-color: var(--color-button-secondary-hover);
      .CheckboxCheckIcon {
        display: none;
      }
      .CheckboxIndeterminateIcon {
        display: initial;
      }
    }
    &:disabled + * {
      border-color: var(--color-border-dark);
      background-color: var(--color-background-white);
      svg {
        color: var(--color-background-dark);
      }
    }

    &:disabled:checked + * {
      border-color: var(--color-border-dark);
      background-color: var(--color-background-dark);
      svg {
        color: var(--color-background-white);
      }
    }
    &:disabled + * + * {
      color: var(--color-background-dark);
    }
    &:focus-visible + * {
      border-color: var(--color-button-secondary-hover);
      background: var(--color-background-white);
      svg {
        color: var(--color-button-secondary-hover);
      }
    }
    &:focus-visible:checked + *,
    &:indeterminate:checked + * {
      background-color: ${dropdownColors.hover};
    }
  }
  &:hover > input:not(:disabled) + *,
  &:focus > input:not(:disabled) + * {
    border-color: var(--color-button-secondary-hover);
    background: var(--color-background-white);
    svg {
      color: var(--color-button-secondary-hover);
    }
  }
  &:hover > input:checked:not(:disabled) + *,
  &:focus-visible > input:checked:not(:disabled) + *,
  &:indeterminate > input:checked:not(:disabled) + * {
    background-color: ${dropdownColors.hover};
  }
  ${({ error }) =>
    error &&
    css`
      > input {
        & + *,
        &:checked + *,
        &:indeterminate + * {
          border-color: var(--color-button-warning-hover);
        }
      }
      &:hover > input:not(:disabled) + * {
        border-color: var(--color-button-warning-hover);
      }
    `}
`;

const VisualCheckbox = styled.span`
  box-sizing: content-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border-dark);
  background-color: var(--color-background-white);
  border-radius: 2px;
  &:not(:last-child) {
    margin-right: 10px;
  }
  width: 15px;
  height: 15px;
  && svg {
    color: var(--color-background-white);
    height: auto;
  }
  && .CheckboxCheckIcon svg {
    width: 13px;
  }
  && .CheckboxIndeterminateIcon svg {
    width: 11px;
  }
  ${({ variant }) =>
    variant === 'underline' &&
    css`
      width: 12px;
      height: 12px;
      && .CheckboxCheckIcon svg {
        width: 10px;
      }
      && .CheckboxIndeterminateIcon svg {
        width: 8px;
      }
    `}
`;

const Checkbox = forwardRef(
  (
    { label, variant, error, indeterminate, onChange, className, ...props },
    ref
  ) => {
    const inputRef = useRef();
    const mergeRef = useCallback(
      (node) => {
        applyRef(inputRef, node);
        applyRef(ref, node);
      },
      [ref]
    );
    useEffect(() => {
      if (inputRef.current) inputRef.current.indeterminate = indeterminate;
    }, [indeterminate]);

    return (
      <Label className={className} error={error}>
        <input
          ref={mergeRef}
          type="checkbox"
          aria-checked={props.checked}
          onChange={(ev) => {
            if (onChange) onChange(ev.target.checked, ev);
          }}
          {...props}
        />
        <VisualCheckbox variant={variant}>
          <Icon className="CheckboxCheckIcon" icon="check" />
          <Icon className="CheckboxIndeterminateIcon" icon="minus" />
        </VisualCheckbox>
        {typeof label !== 'string' && label}
        {typeof label === 'string' && (
          <TextEllipsisWithTooltip as="span">{label}</TextEllipsisWithTooltip>
        )}
      </Label>
    );
  }
);

export default Checkbox;
